import { Box, Typography } from '@mui/material';

import { percentLine, statusLabels, type WindowMode } from '../benchmarkMonitoring';
import { BENCH } from '../tokens';
import type { MonitoringCounts } from '../types';

export const STATUS_COLORS = [
  BENCH.status.scored,
  BENCH.status.toScore,
  BENCH.status.inProgress,
  BENCH.status.notStarted,
] as const;

function segments(counts: MonitoringCounts): number[] {
  return [counts.scored, counts.toScore, counts.inProgress, counts.notStarted];
}

export function ProgressBar({ counts, mode }: { counts: MonitoringCounts; mode: WindowMode }) {
  const labels = statusLabels(mode);
  const values = segments(counts);
  const description = values.map((v, i) => `${v} ${(labels[i] ?? '').toLowerCase()}`).join(', ');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
      <Typography
        component="span"
        data-testid="percent-line"
        sx={{ fontFamily: BENCH.font, fontSize: '12px', lineHeight: '16px', fontWeight: 700, color: BENCH.ink, whiteSpace: 'nowrap' }}
      >
        {percentLine(counts)}
      </Typography>
      <Box
        role="img"
        aria-label={description}
        data-testid="progress-bar"
        sx={{ display: 'flex', height: 12, borderRadius: '4px', overflow: 'hidden' }}
      >
        {values.map((value, i) =>
          value > 0 ? (
            <Box
              key={STATUS_COLORS[i]}
              data-testid="progress-segment"
              sx={{ flexBasis: 0, bgcolor: STATUS_COLORS[i] }}
              style={{ flexGrow: value }}
            />
          ) : null,
        )}
      </Box>
    </Box>
  );
}

export function StatusDot({ color }: { color: string }) {
  return (
    <Box
      component="span"
      aria-hidden="true"
      data-testid="status-dot"
      sx={{ width: 8, height: 8, borderRadius: '9999px', flexShrink: 0, display: 'inline-block', bgcolor: color }}
    />
  );
}
